// Fleet: station releases (spec/14 §12, F-STN-12, spec/10 §8, spec/14 §12). One page, three blocks:
//
//  - **Rollouts.** A card per channel (`stable`, `beta`): the target version (a registered release that is not
//    halted), the share of the channel's stations offered it (a slider and a number, 0–100), Save, and an
//    immediate Halt / Resume. Raising the share or changing the target asks first — it changes the software on
//    real venues' PCs — and a `422` (the version is not registered) is said in words.
//  - **Fleet.** How many stations run each client version and what their updaters are doing, as bars whose
//    words carry the number (the bar is decoration; a count is never colour or length alone).
//  - **Releases.** The registered releases, newest first, with Halt / Resume on each, and "Register release":
//    a dialog taking the signed manifest the offline release tool printed. The server verifies the signature;
//    `409` (the version is registered) and `422` (the signature does not verify) are said in words.
//  - **Boot server releases** (`BootServerReleases.tsx`): the Clavian Boot server's own releases, each made current on a
//    channel by hand; boot servers download it and install it only when the venue chooses Update now.
//
// `hq:releases` gates every route here; the page is for a role holding it, anyone else is told it is not theirs.

import type { SetStationRollout, StationFleet, StationRelease, StationReleaseChannel, StationRollout } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, DialogClose, EmptyState, Field, InfoTip, SelectField, Skeleton, Table, useButtonFeedback, useToast, VisuallyHidden, type TableColumn } from '@clavian/ui-web';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { problemCode, problemDetail, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Icon } from '../../shell/nav-icons';
import { LoadFailure } from '../../shell/LoadFailure';
import { Notice, Section } from '../../ui';
import { RequirePlatformPermission } from '../access';
import { HQ_RELEASES } from '../sections';
import { BootServerReleases } from './BootServerReleases';
import { useRegisterStationRelease, useSetStationReleaseHalted, useSetStationRollout, useStationFleet, useStationReleases, useStationRollouts } from './queries';

import './releases.css';
import { HqHeading } from '../heading';

const COMPACT_JWS = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const NO_TARGET = 'none';

export function StationReleasesPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_RELEASES}>
      <StationReleases />
    </RequirePlatformPermission>
  );
}

function StationReleases() {
  const { t } = useTranslation('hq');
  const releases = useStationReleases();
  return (
    <div className="console-page">
      <header className="console-releases-head">
        <HqHeading title={t('releases.title')} tip={t('releases.intro')} />
      </header>
      <Rollouts releases={releases.data} />
      <Fleet />
      <Releases releases={releases.data} error={releases.isError ? releases.error : undefined} onRetry={() => void releases.refetch()} />
      <BootServerReleases />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Rollouts
// ---------------------------------------------------------------------------------------------

function Rollouts({ releases }: { readonly releases: readonly StationRelease[] | undefined }) {
  const { t } = useTranslation('hq');
  const rollouts = useStationRollouts();
  let body: ReactNode;
  if (rollouts.data === undefined) {
    body = rollouts.isError ? (
      <LoadFailure error={rollouts.error} message={t('releases.rollouts.failed')} onRetry={() => void rollouts.refetch()} />
    ) : (
      <div className="console-releases-rollouts" aria-hidden="true">
        <Skeleton height="16rem" />
        <Skeleton height="16rem" />
      </div>
    );
  } else {
    body = (
      <div className="console-releases-rollouts">
        {rollouts.data.map((rollout) => (
          // Keyed by when it last changed, so a save (or someone else's) re-seeds the card's draft.
          <RolloutCard key={`${rollout.channel}-${rollout.updatedAt ?? 'never'}-${String(rollout.halted)}`} rollout={rollout} releases={releases ?? []} />
        ))}
      </div>
    );
  }
  return (
    <Section title={t('releases.rollouts.title')} actions={<InfoTip>{t('releases.rollouts.intro')}</InfoTip>}>
      {body}
    </Section>
  );
}

function RolloutCard({ rollout, releases }: { readonly rollout: StationRollout; readonly releases: readonly StationRelease[] }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const format = useVenueFormatter();
  const { show } = useToast();
  const save = useSetStationRollout();
  // Save stays on the page: the button shows a check for a moment once the rollout is saved.
  const saveFeedback = useButtonFeedback(save);
  const toggle = useSetStationRollout();
  const headingId = useId();
  const [target, setTarget] = useState<string>(rollout.targetVersion ?? NO_TARGET);
  const [percentage, setPercentage] = useState<number>(rollout.percentage);
  const [confirming, setConfirming] = useState(false);
  const channel = t(`releases.channel.${rollout.channel}`);

  const targetVersion = target === NO_TARGET ? null : target;
  const dirty = targetVersion !== rollout.targetVersion || percentage !== rollout.percentage;
  // Offering the software to more PCs — a new target, or a larger share of one — asks first.
  const widens = targetVersion !== null && percentage > 0 && (targetVersion !== rollout.targetVersion || percentage > rollout.percentage);

  const options = [
    { value: NO_TARGET, label: t('releases.rollouts.noTarget') },
    ...releases
      .filter((release) => !release.halted || release.version === rollout.targetVersion)
      .map((release) => ({
        value: release.version,
        label: release.halted ? t('releases.rollouts.haltedOption', { version: release.version }) : release.version,
      })),
  ];
  // The saved target stays choosable even before the releases have loaded.
  if (rollout.targetVersion !== null && !options.some((option) => option.value === rollout.targetVersion)) {
    options.push({ value: rollout.targetVersion, label: rollout.targetVersion });
  }

  const put = (body: SetStationRollout, done: string) => {
    save.mutate(
      { channel: rollout.channel, body },
      {
        onSuccess: () => {
          setConfirming(false);
          show({ tone: 'ok', title: done });
        },
        onError: () => setConfirming(false),
      },
    );
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!dirty || save.isPending) return;
    if (widens) {
      setConfirming(true);
      return;
    }
    put({ targetVersion, percentage, halted: rollout.halted }, t('releases.rollouts.saved', { channel }));
  };
  const flipHalt = () => {
    if (toggle.isPending) return;
    const halted = !rollout.halted;
    toggle.mutate(
      { channel: rollout.channel, body: { targetVersion: rollout.targetVersion, percentage: rollout.percentage, halted } },
      {
        onSuccess: () => show({ tone: 'ok', title: t(halted ? 'releases.rollouts.haltedDone' : 'releases.rollouts.resumedDone', { channel }) }),
        onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('releases.rollouts.saveFailed') }) }),
      },
    );
  };

  const saveError = save.isError ? rolloutFailure(save.error, t, (error, fallback) => problems.text(error, { fallback })) : undefined;

  return (
    <article
      className="console-releases-rollout console-rail"
      data-rail={rollout.halted ? 'warn' : rollout.targetVersion === null || rollout.percentage === 0 ? 'quiet' : 'live'}
      aria-labelledby={headingId}
    >
      <header className="console-releases-rollout__head">
        <div className="console-releases-rollout__title">
          <h3 id={headingId}>{t('releases.rollouts.cardTitle', { channel })}</h3>
          {rollout.halted ? <Badge tone="warn">{t('releases.halted')}</Badge> : null}
        </div>
        <Button
          variant={rollout.halted ? 'secondary' : 'danger'}
          size="sm"
          icon={<Icon name={rollout.halted ? 'launch' : 'stop'} />}
          busy={toggle.isPending}
          label={t(rollout.halted ? 'releases.rollouts.resumeLabel' : 'releases.rollouts.haltLabel', { channel })}
          onClick={flipHalt}
        >
          <span>{t(rollout.halted ? 'releases.rollouts.resume' : 'releases.rollouts.halt')}</span>
        </Button>
      </header>
      <p className="console-releases-rollout__summary">
        {rollout.targetVersion === null
          ? t('releases.rollouts.summaryNone')
          : t('releases.rollouts.summary', { version: rollout.targetVersion, percentage: rollout.percentage })}
      </p>
      <p className="console-muted console-releases-rollout__when">
        {rollout.updatedAt === null ? t('releases.rollouts.neverSet') : t('releases.rollouts.updated', { when: format.dateTime(rollout.updatedAt, { date: 'medium', time: 'short' }) })}
      </p>
      {rollout.halted ? (
        <Notice tone="warn" role="note">
          {t('releases.rollouts.haltedNote')}
        </Notice>
      ) : null}

      <form className="console-releases-rollout__form" noValidate onSubmit={submit}>
        <SelectField label={t('releases.rollouts.target')} description={t('releases.rollouts.targetHint')} options={options} value={target} onValueChange={setTarget} />
        <Field label={t('releases.rollouts.percentage')} description={t('releases.rollouts.percentageHint')}>
          {(control) => (
            <div className="console-releases-share">
              <input
                id={control.id}
                aria-describedby={control['aria-describedby']}
                type="range"
                className="console-releases-share__slider"
                min={0}
                max={100}
                step={1}
                value={percentage}
                aria-valuetext={t('releases.rollouts.percentageValue', { value: percentage })}
                onChange={(event) => setPercentage(Number(event.target.value))}
              />
              <input
                type="number"
                inputMode="numeric"
                className="clav-input console-releases-share__number"
                min={0}
                max={100}
                step={1}
                value={percentage}
                aria-label={t('releases.rollouts.percentageNumber')}
                onChange={(event) => {
                  const next = Math.round(Number(event.target.value));
                  if (Number.isFinite(next)) setPercentage(Math.min(100, Math.max(0, next)));
                }}
              />
              <span className="console-releases-share__unit" aria-hidden="true">
                %
              </span>
            </div>
          )}
        </Field>
        {saveError === undefined ? null : (
          <p className="console-releases-error" role="alert">
            {saveError}
          </p>
        )}
        <div className="console-releases-rollout__actions">
          <Button type="submit" variant="primary" {...saveFeedback} disabled={!dirty} disabledReason={t('releases.rollouts.noChanges')} label={t('releases.rollouts.saveLabel', { channel })}>
            <span>{t('releases.rollouts.save')}</span>
          </Button>
          {dirty ? null : <span className="console-muted">{t('releases.rollouts.noChanges')}</span>}
        </div>
      </form>

      <Dialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('releases.rollouts.confirm.title', { version: targetVersion ?? '', channel })}
        description={t('releases.rollouts.confirm.description', { version: targetVersion ?? '', channel, percentage })}
        footer={
          <>
            <DialogClose asChild>
              <Button>{t('releases.rollouts.confirm.cancel')}</Button>
            </DialogClose>
            <Button
              variant="primary"
              busy={save.isPending}
              onClick={() => put({ targetVersion, percentage, halted: rollout.halted }, t('releases.rollouts.saved', { channel }))}
            >
              {t('releases.rollouts.confirm.submit')}
            </Button>
          </>
        }
      />
    </article>
  );
}

function rolloutFailure(error: unknown, t: (key: string) => string, plain: (error: unknown, fallback: string) => string): string {
  if (problemCode(error) === PROBLEM.unprocessable) return t('releases.rollouts.unregistered');
  return plain(error, t('releases.rollouts.saveFailed'));
}

// ---------------------------------------------------------------------------------------------
// Fleet
// ---------------------------------------------------------------------------------------------

function Fleet() {
  const { t } = useTranslation('hq');
  const fleet = useStationFleet();
  let body: ReactNode;
  if (fleet.data === undefined) {
    body = fleet.isError ? (
      <LoadFailure error={fleet.error} message={t('releases.fleet.failed')} onRetry={() => void fleet.refetch()} />
    ) : (
      <Skeleton height="8rem" />
    );
  } else {
    body = <FleetBody fleet={fleet.data} />;
  }
  return (
    <Section title={t('releases.fleet.title')} actions={<InfoTip>{t('releases.fleet.intro')}</InfoTip>}>
      {body}
    </Section>
  );
}

function FleetBody({ fleet }: { readonly fleet: StationFleet }) {
  const { t } = useTranslation('hq');
  if (fleet.reporting === 0) return <EmptyState size="compact" icon={<Icon name="info" />} title={t('releases.fleet.none')} />;
  const stateTotal = fleet.states.reduce((sum, row) => sum + row.stations, 0);
  return (
    <>
      <p className="console-releases-fleet__total">{t('releases.fleet.reporting', { count: fleet.reporting })}</p>
      <div className="console-releases-fleet">
        <Bars title={t('releases.fleet.byVersion')} total={fleet.reporting} rows={fleet.versions.map((row) => ({ key: row.clientVersion, label: row.clientVersion, count: row.stations }))} />
        <Bars
          title={t('releases.fleet.byState')}
          total={stateTotal}
          rows={fleet.states.map((row) => ({
            key: row.state,
            label: t(`releases.fleet.state.${row.state}`),
            count: row.stations,
            warn: row.state === 'failed' || row.state === 'rolled_back',
          }))}
        />
      </div>
    </>
  );
}

interface BarRow {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly warn?: boolean;
}

/** Horizontal bars: the words say the count and share; the bar only draws it. */
function Bars({ title, total, rows }: { readonly title: string; readonly total: number; readonly rows: readonly BarRow[] }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const headingId = useId();
  return (
    <div className="console-releases-bars">
      <h3 id={headingId} className="console-releases-bars__title">
        {title}
      </h3>
      <ul aria-labelledby={headingId}>
        {rows.map((row) => {
          const share = total === 0 ? 0 : row.count / total;
          return (
            <li key={row.key} className="console-releases-bar">
              <span className="console-releases-bar__text" aria-hidden="true">
                <span className="console-releases-bar__label">{row.label}</span>
                <span className="console-num console-releases-bar__count">
                  {format.number(row.count)} · {format.percent(share)}
                </span>
              </span>
              <VisuallyHidden>{t('releases.fleet.row', { label: row.label, count: row.count, share: format.percent(share) })}</VisuallyHidden>
              <span className="console-releases-bar__track" aria-hidden="true">
                <span className={row.warn === true ? 'console-releases-bar__fill console-releases-bar__fill--warn' : 'console-releases-bar__fill'} style={{ width: `${Math.round(share * 100)}%` }} />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Releases
// ---------------------------------------------------------------------------------------------

function Releases({ releases, error, onRetry }: { readonly releases: readonly StationRelease[] | undefined; readonly error: unknown; readonly onRetry: () => void }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const [registering, setRegistering] = useState(false);

  const columns: readonly TableColumn<StationRelease>[] = [
    { key: 'version', header: t('releases.list.column.version'), cell: (release) => <span className="console-num console-releases-version">{release.version}</span> },
    { key: 'channel', header: t('releases.list.column.channel'), cell: (release) => <ChannelBadge channel={release.channel} /> },
    {
      key: 'released',
      header: t('releases.list.column.released'),
      cell: (release) => <span className="console-num">{format.dateTime(release.manifest.releasedAt, { date: 'medium', time: 'short' })}</span>,
    },
    { key: 'kiosk', header: t('releases.list.column.kiosk'), cell: (release) => <span className="console-num">{release.manifest.kiosk.version}</span> },
    {
      key: 'size',
      header: t('releases.list.column.size'),
      numeric: true,
      cell: (release) => (
        <span className="console-num">{format.number(release.manifest.msi.size / 1_048_576, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 })}</span>
      ),
    },
    {
      key: 'status',
      header: t('releases.list.column.status'),
      cell: (release) => (release.halted ? <Badge tone="warn">{t('releases.halted')}</Badge> : <Badge tone="ok">{t('releases.active')}</Badge>),
    },
    { key: 'actions', header: t('releases.list.column.actions'), cell: (release) => <HaltRelease release={release} /> },
  ];

  let body: ReactNode;
  if (releases === undefined) {
    body = error === undefined ? <Skeleton height="10rem" /> : <LoadFailure error={error} message={t('releases.failed')} onRetry={onRetry} />;
  } else if (releases.length === 0) {
    body = (
      <EmptyState
        size="compact"
        icon={<Icon name="upload" />}
        title={t('releases.list.empty')}
        action={
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setRegistering(true)}>
            {t('releases.register.action')}
          </Button>
        }
      />
    );
  } else {
    body = <Table caption={t('releases.list.caption')} captionHidden columns={columns} rows={releases} rowKey={(release) => release.version} />;
  }

  return (
    <Section
      title={t('releases.list.title')}
      body={releases !== undefined && releases.length > 0 ? 'rows' : 'padded'}
      actions={
        <>
          <InfoTip>{t('releases.list.intro')}</InfoTip>
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setRegistering(true)}>
            {t('releases.register.action')}
          </Button>
        </>
      }
    >
      {body}
      <RegisterReleaseDialog open={registering} onOpenChange={setRegistering} />
    </Section>
  );
}

function ChannelBadge({ channel }: { readonly channel: StationReleaseChannel }) {
  const { t } = useTranslation('hq');
  return <Badge tone={channel === 'stable' ? 'neutral' : 'accent'}>{t(`releases.channel.${channel}`)}</Badge>;
}

function HaltRelease({ release }: { readonly release: StationRelease }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const mutation = useSetStationReleaseHalted();
  const halted = !release.halted;
  return (
    <Button
      variant={release.halted ? 'secondary' : 'ghost'}
      size="sm"
      icon={<Icon name={release.halted ? 'launch' : 'stop'} />}
      busy={mutation.isPending}
      label={t(release.halted ? 'releases.list.resumeLabel' : 'releases.list.haltLabel', { version: release.version })}
      onClick={() => {
        if (mutation.isPending) return;
        mutation.mutate(
          { version: release.version, halted },
          {
            onSuccess: () => show({ tone: 'ok', title: t(halted ? 'releases.list.halted' : 'releases.list.resumed', { version: release.version }) }),
            onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('releases.list.changeFailed', { version: release.version }) }) }),
          },
        );
      }}
    >
      <span>{t(release.halted ? 'releases.list.resume' : 'releases.list.halt')}</span>
    </Button>
  );
}

function RegisterReleaseDialog({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" dirty="auto" title={t('releases.register.title')} description={t('releases.register.description')}>
      {open ? <RegisterReleaseForm onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function RegisterReleaseForm({ onDone }: { readonly onDone: () => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const register = useRegisterStationRelease();
  const [text, setText] = useState('');
  const [refused, setRefused] = useState<string | undefined>(undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (register.isPending) return;
    const manifest = text.trim();
    const local = manifest === '' ? t('releases.register.required') : COMPACT_JWS.test(manifest) ? undefined : t('releases.register.malformed');
    setRefused(local);
    if (local !== undefined) return;
    register.mutate(manifest, {
      onSuccess: (release) => {
        show({ tone: 'ok', title: t('releases.register.done', { version: release.version }) });
        onDone();
      },
    });
  };

  const serverError = register.isError ? registerFailure(register.error, t, (error, fallback) => problems.text(error, { fallback })) : undefined;

  return (
    <form className="console-releases-register" noValidate onSubmit={submit}>
      <Field label={t('releases.register.manifest')} description={t('releases.register.manifestHint')} error={refused} required>
        {(control) => (
          <textarea
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            required
            className="clav-input console-releases-register__manifest"
            rows={8}
            spellCheck={false}
            autoComplete="off"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>
      {serverError === undefined ? null : (
        <Notice tone="danger" role="alert" title={serverError.title}>
          {serverError.detail === undefined ? null : <p>{serverError.detail}</p>}
        </Notice>
      )}
      <div className="console-releases-register__actions">
        <Button type="button" onClick={onDone}>
          {t('releases.register.cancel')}
        </Button>
        <Button type="submit" variant="primary" icon={<Icon name="upload" />} busy={register.isPending}>
          {t('releases.register.submit')}
        </Button>
      </div>
    </form>
  );
}

/** A refused registration in words: the problem's kind, and the server's own detail when it gave one. */
function registerFailure(
  error: unknown,
  t: (key: string) => string,
  plain: (error: unknown, fallback: string) => string,
): { readonly title: string; readonly detail: string | undefined } {
  const detail = problemDetail(error);
  if (problemCode(error) === PROBLEM.conflict) return { title: t('releases.register.conflict'), detail };
  if (problemCode(error) === PROBLEM.unprocessable) return { title: t('releases.register.unprocessable'), detail };
  return { title: plain(error, t('releases.register.failedGeneric')), detail };
}
